import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { createClient } from '@supabase/supabase-js';
import { useNavigate, Link } from 'react-router-dom';

const supabase = createClient(
  'https://rmlppxyybfoexdiifhdh.supabase.co',
  'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InJtbHBweHl5YmZvZXhkaWlmaGRoIiwicm9sZSI6ImFub24iLCJpYXQiOjE3MzY3NzQwODgsImV4cCI6MjA1MjM1MDA4OH0.FnUKwK0FxK-yuho4kIjcrOdD0yrgkNXwxD-fKJebnqc'
);

const AdminContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
`;

const LoginContainer = styled.div`
  max-width: 400px;
  margin: 100px auto;
  padding: 2rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 1rem;
  border: 1px solid #ddd;
  border-radius: 4px;
`;

const Button = styled.button`
  background: black;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  
  &:hover {
    opacity: 0.9;
  }
`;

const QuoteRow = styled.div`
  display: grid;
  grid-template-columns: 60px auto 100px;
  gap: 1rem;
  padding: 1rem;
  border-bottom: 1px solid #eee;
  align-items: center;
  user-select: none;
  
  > div {
    padding: 0 1rem;
    
    &:not(:last-child) {
      border-right: 1px solid #eee;
    }
  }
`;

const IdCell = styled.div`
  color: #666;
  font-size: 0.875rem;
  font-weight: 500;
`;

const QuoteCell = styled.div`
  p {
    margin: 0 0 0.5rem 0;
  }
  
  small {
    color: #666;
  }
`;

const CheckboxCell = styled.div`
  display: flex;
  justify-content: center;
`;

const Checkbox = styled.input`
  width: 20px;
  height: 20px;
  cursor: pointer;
`;

const AdminQuotes = () => {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quotes, setQuotes] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [lastCheckedState, setLastCheckedState] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchQuotes();
    });

    // Set up auth listener
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      if (session) fetchQuotes();
    });

    return () => subscription.unsubscribe();
  }, []);

  const fetchQuotes = async () => {
    try {
      const { data, error } = await supabase
        .from('tweets')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setQuotes(data);

      // Set up real-time subscription
      const subscription = supabase
        .channel('tweets_changes')
        .on('postgres_changes', 
          { 
            event: '*', 
            schema: 'public', 
            table: 'tweets' 
          }, 
          payload => {
            if (payload.new) {
              setQuotes(currentQuotes => 
                currentQuotes.map(quote =>
                  quote.id === payload.new.id ? payload.new : quote
                )
              );
            }
          }
        )
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    } catch (error) {
      console.error('Error fetching quotes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (error) {
      alert(error.message);
    }
  };

  const handleCheckboxChange = async (id, currentValue) => {
    try {
      const newValue = !currentValue;
      
      const { error } = await supabase
        .from('tweets')
        .update({ include_in_search: newValue })
        .eq('id', id);

      if (error) {
        throw error;
      }

      setLastCheckedState(newValue);
    } catch (error) {
      console.error('Error updating quote:', error);
      alert('Error updating quote: ' + error.message);
    }
  };

  const handleMouseDown = (id, currentValue) => {
    setIsDragging(true);
    handleCheckboxChange(id, currentValue);
  };

  const handleMouseEnter = (id, currentValue) => {
    if (isDragging && lastCheckedState !== null) {
      handleCheckboxChange(id, !lastCheckedState);
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setLastCheckedState(null);
  };

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    return () => document.removeEventListener('mouseup', handleMouseUp);
  }, []);

  if (!session) {
    return (
      <LoginContainer>
        <h2>Admin Login</h2>
        <form onSubmit={handleLogin}>
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <Button type="submit">Login</Button>
        </form>
        <p>
          <Link to="/reset-password">Reset Password</Link>
        </p>
      </LoginContainer>
    );
  }

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <AdminContainer>
      <h1>Manage Quotes</h1>
      <Button onClick={() => supabase.auth.signOut()}>Sign Out</Button>
      
      {quotes.map((quote) => (
        <QuoteRow 
          key={quote.id}
          onMouseEnter={() => handleMouseEnter(quote.id, quote.include_in_search)}
        >
          <IdCell>
            #{quote.id}
          </IdCell>
          <QuoteCell>
            <p>{quote.text}</p>
            <small>
              {new Date(quote.created_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </small>
          </QuoteCell>
          <CheckboxCell>
            <Checkbox
              type="checkbox"
              checked={quote.include_in_search}
              onChange={() => handleCheckboxChange(quote.id, quote.include_in_search)}
              onMouseDown={() => handleMouseDown(quote.id, quote.include_in_search)}
            />
          </CheckboxCell>
        </QuoteRow>
      ))}
    </AdminContainer>
  );
};

export default AdminQuotes; 